<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div v-if="playlist.id">
          Edit playlist
        </div>
        <div v-else-if="parentPlaylist">
          <div>Create child playlist</div>
          <div class="body-2">
            Parent Playlist:
            <router-link :to="{ name: 'playlist', params: { id: parentPlaylist.id }}">
              {{ parentPlaylist.title }}
            </router-link>
          </div>
        </div>
        <div v-else>
          Create new playlist
        </div>
      </v-card-title>
    </template>
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <div class="mb-2 font-weight-bold">
          Playlist Title
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="playlistDirty.title"
            outlined
            :error-messages="errors"
            placeholder="Enter playlist title"
          />
        </validation-provider>

        <v-row
          align="center"
          no-gutters
          class="mb-5"
        >
          <v-col>
            <div class="font-weight-bold">
              Playlist Songs Content
            </div>
            <div
              v-if="contentMethod !== 'parent'"
              class="text--secondary caption"
            >
              Close and pick other to change
            </div>
          </v-col>
          <v-col cols="auto">
            <v-chip
              color="secondary"
              dark
              class="ma-0"
            >
              {{ contentMethod === 'handpicked' ? 'Handpicked songs' : (contentMethod === 'parent' ? 'Based on parent playlist' : 'By Attributes') }}
            </v-chip>
          </v-col>
        </v-row>

        <div v-if="contentMethod === 'attributes'">
          <div
            v-for="attribute in attributes"
            :key="attribute.id"
          >
            <v-select
              v-model="selectedAttributesDirty[attribute.id]"
              :label="attribute.title"
              :items="attribute.values"
              item-text="value"
              item-value="id"
              chips
              deletable-chips
              small-chips
              multiple
              outlined
            />
          </div>
        </div>
        <div
          v-else-if="contentMethod !== 'parent'"
          class="mb-6"
        >
          <v-card
            outlined
          >
            <v-card-text v-if="!playlistDirty.id">
              This playlist will contain <strong>{{ selectedLibraryFiles.length }} handpicked songs</strong>. To select
              different songs, close this dialog and re-select.
            </v-card-text>
            <v-card-text v-else>
              Playlist contains <strong>{{ playlist.library_files_count }} handpicked songs</strong>.<br><br>
              To remove songs: find the songs in playlist, click delete.
              To add more songs: go to Music Library, select songs and add to this playlist with "Add to Playlist".
            </v-card-text>
          </v-card>
        </div>

        <div class="mb-2 font-weight-bold">
          Additional Playlist Description
        </div>
        <v-textarea
          v-model="playlistDirty.description"
          outlined
          placeholder="Enter playlist description"
        />

        <div class="mb-2 font-weight-bold">
          For Client
        </div>
        <v-autocomplete
          v-model="playlistDirty.client_id"
          solo
          flat
          outlined
          hint="Select if playlist is specific for a certain client"
          persistent-hint
          :items="clients.data"
          :loading="clients.loading"
          clearable
          item-text="name"
          item-value="id"
          placeholder="All Clients"
        />
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="playlistDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deletePlaylist()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import PlaylistModel from '@/models/PlaylistModel'
import Playlists from '@/services/Playlists'
import Clients from '@/services/Clients'

export default {
  name: 'EditPlaylistDialog',
  components: { ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    playlist: {
      type: Object,
      default: () => cloneDeep(PlaylistModel.emptyModel)
    },
    selectedLibraryFiles: {
      type: Array,
      default: () => ([])
    },
    selectedAttributes: {
      type: Object,
      default: () => ({})
    },
    parentPlaylist: {
      type: Object,
      default: () => undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      playlistDirty: cloneDeep(this.playlist),
      selectedAttributesDirty: cloneDeep(this.selectedAttributes),
      submitting: false,
      deleting: false,
      clients: {
        loading: false,
        data: []
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    contentMethod() {
      if (this.playlist.id) {
        if (this.playlist.attribute_values.length > 0) return 'attributes'
        else if (this.playlist.is_child) return 'parent'
        else return 'handpicked'
      }

      if (this.parentPlaylist) return 'parent'

      if (this.selectedLibraryFiles.length > 0) return 'handpicked'

      return 'attributes'
    },
    attributes() {
      return this.$store.state.attributes.data
    }
  },
  watch: {
    modalVisible(visible) {
      if (!visible) return

      this.playlistDirty = cloneDeep(this.playlist)
      if (!this.playlist.id) {
        this.selectedAttributesDirty = cloneDeep(this.selectedAttributes)

        if (this.parentPlaylist) {
          this.playlistDirty.title = this.parentPlaylist.title + ' kopija'
        } else {
          const titleParts = []

          for (const attributeId of Object.keys(this.selectedAttributes)) {
            const attributeTitleParts = []

            for (const attributeValue of this.selectedAttributes[attributeId]) {
              attributeTitleParts.push(this.attributes.find(a => a.id === Number(attributeId))?.values.find(v => v.id === Number(attributeValue))?.value)
            }

            titleParts.push(attributeTitleParts.filter(part => !!part).join('/'))
          }

          this.playlistDirty.title = titleParts.filter(p => !!p).join(' - ')
        }
      } else if (this.playlist.attribute_values.length > 0) {
        const attributes = {}

        for (const attributeValue of this.playlist.attribute_values) {
          if (!attributes[attributeValue.attribute.id]) {
            attributes[attributeValue.attribute.id] = [attributeValue.id]
          } else {
            attributes[attributeValue.attribute.id].push(attributeValue.id)
          }
        }

        this.selectedAttributesDirty = attributes
      }
    }
  },
  created() {
    this.fetchClients()
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        if (this.playlistDirty.id) {
          const data = {
            title: this.playlistDirty.title,
            description: this.playlistDirty.description,
            content_type: PlaylistModel.CONTENT_TYPE_MUSIC,
            client_id: this.playlistDirty.client_id
          }

          if (this.contentMethod === 'attributes') {
            data.attribute_values = this.selectedAttributesDirty
          }

          const result = await Playlists.update(this.playlistDirty.id, data)

          this.$emit('playlist:updated', result)

          this.$store.dispatch('alert', {
            text: 'Playlist successfully updated',
            color: 'success'
          })
        } else {
          const data = {
            title: this.playlistDirty.title,
            description: this.playlistDirty.description,
            content_type: PlaylistModel.CONTENT_TYPE_MUSIC,
            library_files: this.contentMethod === 'handpicked' ? this.selectedLibraryFiles.map(f => f.id) : [],
            attribute_values: this.contentMethod === 'attributes' ? this.selectedAttributesDirty : [],
            client_id: this.playlistDirty.client_id
          }

          let result

          if (this.parentPlaylist) {
            result = await Playlists.clone(this.parentPlaylist.id, data)
          } else {
            result = await Playlists.store(data)
          }

          this.$emit('playlist:created', result)

          this.$store.dispatch('alert', {
            text: 'Playlist successfully created',
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting playlist',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deletePlaylist() {
      this.deleting = true

      try {
        const result = await Playlists.delete([this.playlistDirty.id])

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('playlist:updated', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    }
  }
}
</script>
